import { Heart } from 'lucide-react';

import { useTrackDetailQuery } from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { Button } from '../../design-system/button.js';
import { cn } from '../../design-system/cn.js';
import { usePlaybackTransport } from '../../player/player-context.js';
import { useFavoriteActionState } from './favorite-action.js';

function PlayerFavoriteControl({
  className,
  connectionActive,
  favorite,
  trackId,
}: {
  className: string | undefined;
  connectionActive: boolean;
  favorite: boolean | null;
  trackId: string;
}) {
  const { displayedFavorite, failure, pending, toggle } = useFavoriteActionState({
    favorite,
    trackId,
  });

  return (
    <>
      <Button
        aria-busy={pending || undefined}
        aria-disabled={pending || undefined}
        aria-label={displayedFavorite ? 'Unlike track' : 'Like track'}
        aria-pressed={displayedFavorite}
        className={cn(
          className,
          displayedFavorite && 'text-accent',
          failure ? 'ring-1 ring-err' : undefined,
        )}
        disabled={!connectionActive}
        onClick={() => {
          if (!pending) void toggle();
        }}
        size="icon"
        title={failure ? 'Favorite update failed. Try again.' : undefined}
        variant="ghost"
      >
        <Heart aria-hidden="true" className={cn('size-5', displayedFavorite && 'fill-current')} />
      </Button>
      {failure ? (
        <span className="sr-only" role="status">
          Favorite update failed.
        </span>
      ) : null}
    </>
  );
}

export function CurrentTrackFavoriteButton({ className }: { className?: string }) {
  const player = usePlaybackTransport();
  const { connection, library } = useWorkspace();
  const item = player.item;
  const directAuditItem = item?.sourcePreference === 'direct';
  const detail = useTrackDetailQuery(
    directAuditItem ? undefined : item?.id,
    item && !directAuditItem ? { connection_id: connection.id, library_id: library.id } : undefined,
  );

  if (!item || directAuditItem) return null;

  if (detail.isPending) {
    return (
      <Button
        aria-busy="true"
        aria-label={`Loading like status for ${item.title}`}
        className={className}
        disabled
        size="icon"
        variant="ghost"
      >
        <Heart aria-hidden="true" className="size-5" />
      </Button>
    );
  }

  if (detail.isError) {
    return (
      <Button
        aria-label={`Like status unavailable for ${item.title}`}
        className={cn(className, 'ring-1 ring-err')}
        disabled
        size="icon"
        title="Like status unavailable"
        variant="ghost"
      >
        <Heart aria-hidden="true" className="size-5" />
      </Button>
    );
  }

  return (
    <PlayerFavoriteControl
      className={className}
      connectionActive={connection.status === 'active'}
      favorite={detail.data.favorite}
      trackId={item.id}
    />
  );
}
